import { useEffect, useRef, useState } from 'react';

import { GtoConnectLockup } from '../brand/GtoConnectLockup';
import { Yarlyk } from '../brand/Yarlyk';
import { Icon } from '../components/Icon';
import { formatWhen } from '../lib/format';
import type { MeetingView } from '../lib/api';
import './WaitingScreen.css';

/**
 * Экран ожидания запланированной встречи.
 *
 * Отсчёт идёт по локальным часам, но точка отсчёта пришла с сервера:
 * если часы участника врут, он всё равно попадёт в комнату вовремя —
 * решение о входе принимает сервер, а не эта страница.
 */
export function WaitingScreen({
  meeting,
  onOpen,
}: {
  meeting: MeetingView;
  onOpen: () => void;
}) {
  const opensAt = meeting.opensAt ?? meeting.startsAt ?? Date.now();
  const [now, setNow] = useState(() => Date.now());

  const remaining = Math.max(0, opensAt - now);
  const opened = remaining === 0;

  // Секундная стрелка нужна только пока ждём.
  useEffect(() => {
    if (opened) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [opened]);

  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  useEffect(() => {
    if (!opened) return;
    const timer = setTimeout(() => openRef.current(), 500);
    return () => clearTimeout(timer);
  }, [opened]);

  const parts = split(remaining);

  return (
    <div className={`wait ${opened ? 'wait--open' : ''}`}>
      <div className="grid-bg wait__grid" />
      <div className="aura wait__aura" />

      <main className="wait__main">
        {/* Ярлык только в момент открытия: пока идёт отсчёт, подписывать
            экран незачем — и так видно, что встреча ещё не началась. */}
        {opened && (
          <Yarlyk side="bottom" tone="accent">
            Комната открыта
          </Yarlyk>
        )}

        <h1 className="wait__title">{meeting.title}</h1>

        <p className="wait__when">
          <Icon name="calendar" size={15} />
          {formatWhen(meeting.startsAt)}
        </p>

        {/* Разряды отсчёта — каждый в своей ячейке, как на табло. */}
        <div className="wait__count" role="timer" aria-live="off">
          {opened ? (
            <span className="wait__opening">Открываем комнату</span>
          ) : (
            parts.map((part, i) => (
              <span className="wait__unit" key={part.label}>
                <span className="wait__digits" style={{ '--i': i } as React.CSSProperties}>
                  {part.value}
                </span>
                <span className="wait__unit-label">{part.label}</span>
              </span>
            ))
          )}
        </div>

        <div className="wait__bar" aria-hidden>
          <span className="wait__bar-fill" />
        </div>

        {/* Действий здесь намеренно нет: на этот экран попадают по ссылке,
            копировать её незачем, а на главной гостю делать нечего —
            создавать встречи он всё равно не может. */}
        <p className="wait__note">
          Страница откроет встречу сама. Вход открывается за пять минут до начала.
        </p>
      </main>

      <footer className="wait__footer">
        <GtoConnectLockup markHeight={18} />
      </footer>
    </div>
  );
}

/** Разбивает остаток на значащие разряды: дни отпадают, когда их нет. */
function split(ms: number): { value: string; label: string }[] {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  if (days > 0) {
    return [
      { value: String(days), label: 'дн' },
      { value: pad(hours), label: 'ч' },
      { value: pad(minutes), label: 'мин' },
    ];
  }

  if (hours > 0) {
    return [
      { value: pad(hours), label: 'ч' },
      { value: pad(minutes), label: 'мин' },
      { value: pad(seconds), label: 'с' },
    ];
  }

  return [
    { value: pad(minutes), label: 'мин' },
    { value: pad(seconds), label: 'с' },
  ];
}
